import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationResult } from 'react-query';
import { client } from 'utils/api-client';

interface BulkPayrollExportParams {
  payrollPeriodIds: string[];
}

interface ApiError {
  message?: string;
}

interface BulkPayrollExportResult {
  blob: Blob;
  filename: string;
}

interface BulkPayrollExportError extends Error {
  response?: { data: ApiError };
}

const parseErrorBlob = async (blob: Blob): Promise<ApiError> => {
  try {
    const text = await blob.text();
    return JSON.parse(text) as ApiError;
  } catch {
    return { message: 'An error occurred while generating the export.' };
  }
};

const extractFilenameFromHeader = (contentDisposition: string | undefined): string | null => {
  if (!contentDisposition) return null;
  const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
  return filenameMatch ? filenameMatch[1] : null;
};

const bulkPayrollExport = async (params: BulkPayrollExportParams): Promise<BulkPayrollExportResult> => {
  try {
    const response: AxiosResponse<Blob> = await client.post(
      'payroll-periods/bulk-export/',
      { payroll_period_ids: params.payrollPeriodIds },
      { responseType: 'blob' },
    );
    const filename =
      extractFilenameFromHeader(response.headers['content-disposition']) ||
      `payroll-export-${new Date().toISOString().split('T')[0]}.csv`;
    return { blob: response.data, filename };
  } catch (error) {
    const axiosError = error as AxiosError<Blob>;
    if (axiosError.response?.data instanceof Blob) {
      const parsedError = await parseErrorBlob(axiosError.response.data);
      const enhancedError: BulkPayrollExportError = new Error(parsedError.message);
      enhancedError.response = { data: parsedError };
      throw enhancedError;
    }
    throw error;
  }
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const useBulkPayrollExport = (): UseMutationResult<
  BulkPayrollExportResult,
  BulkPayrollExportError,
  BulkPayrollExportParams
> => {
  return useMutation({
    mutationFn: bulkPayrollExport,
    onSuccess: data => {
      downloadBlob(data.blob, data.filename);
    },
  });
};

export type { BulkPayrollExportError };
export default useBulkPayrollExport;
